// Costs / CAC view — your hourly rate, time invested, and per-channel ad spend.
// Ported from the prototype CostsView: server data via oRPC + TanStack Query,
// shared fmt* helpers, and mutations (settings.update / campaigns.*).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type KeyboardEvent } from 'react';
import { orpc } from '../../lib/orpc';
import { fmtMin, fmtZl } from '../../lib/format';
import { useShell } from '../../shell';
import { Icon } from '../../components/Icon';
import type { CostsSummary } from '@delvero/contracts';

type CampaignRow = CostsSummary['campaigns'][number];

export function CostsView() {
  const { activeAppId, activeApp } = useShell();
  const qc = useQueryClient();

  const invalidate = (): void => {
    qc.invalidateQueries({ queryKey: orpc.costs.key() });
    qc.invalidateQueries({ queryKey: orpc.leads.key() });
    qc.invalidateQueries({ queryKey: orpc.dashboard.key() });
  };

  const summaryQuery = useQuery({
    ...orpc.costs.summary.queryOptions({ input: { appId: activeAppId ?? '' } }),
    enabled: !!activeAppId,
  });

  const updateSettings = useMutation(
    orpc.settings.update.mutationOptions({ onSuccess: invalidate }),
  );
  const updateSpend = useMutation(
    orpc.campaigns.updateSpend.mutationOptions({ onSuccess: invalidate }),
  );
  const createCampaign = useMutation(
    orpc.campaigns.create.mutationOptions({ onSuccess: invalidate }),
  );

  const summary = summaryQuery.data;

  if (!activeAppId || !summary) {
    return (
      <div className="view">
        <div className="card center-pad">
          <span className="spinner" /> Wczytywanie…
        </div>
      </div>
    );
  }

  const addCampaign = (): void => {
    const name = window.prompt('Nazwa kampanii / kanału:');
    if (!name) return;
    createCampaign.mutate({ name });
  };

  return (
    <div className="view">
      <div className="dash-cols costs-cols">
        <div className="card pad">
          <div className="card-title">Moja stawka</div>
          <RateField
            label="Stawka godzinowa (zł/h)"
            value={summary.settings.hourlyRate}
            onCommit={(v) => updateSettings.mutate({ hourlyRate: v })}
          />
          <RateField
            label="Czas na 1 cold mail (min)"
            value={summary.settings.emailMinutes}
            onCommit={(v) => updateSettings.mutate({ emailMinutes: v })}
          />
          <div className="cell-sub" style={{ marginTop: 8 }}>
            Te wartości wyceniają Twój czas w CAC każdego leada.
          </div>
        </div>
        <div className="card pad">
          <div className="card-title">Czas zainwestowany — {activeApp?.name ?? '—'}</div>
          <div className="econ-line">
            <span>
              Calle — {summary.callCount} szt., {fmtMin(summary.callSec)}
            </span>
            <span className="mono">{fmtZl(summary.callCost)}</span>
          </div>
          <div className="econ-line">
            <span>Cold maile — {summary.emails} szt.</span>
            <span className="mono">{fmtZl(summary.mailCost)}</span>
          </div>
          <div className="econ-line">
            <span>Reklamy — wszystkie kanały</span>
            <span className="mono">{fmtZl(summary.adTotal)}</span>
          </div>
          <div className="econ-line total">
            <span>Łączny koszt sprzedaży</span>
            <span className="mono">{fmtZl(summary.totalCost)}</span>
          </div>
          <div className="econ-line">
            <span>Blended CAC ({summary.subCount} subów)</span>
            <span className="mono acc">{summary.subCount ? fmtZl(summary.blendedCac) : '—'}</span>
          </div>
        </div>
      </div>

      <div className="card pad">
        <div className="card-title">
          Kanały i kampanie
          <button className="btn slim-btn" onClick={addCampaign}>
            <Icon name="plus" size={12} /> Dodaj kanał
          </button>
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th>Kanał</th>
              <th className="num">Wydatki</th>
              <th className="num">Leady</th>
              <th className="num">Koszt / lead</th>
              <th className="num">Suby</th>
              <th className="num">Koszt reklamy / sub</th>
            </tr>
          </thead>
          <tbody>
            {summary.campaigns.map((c) => (
              <CampaignRowView
                key={c.id}
                campaign={c}
                onCommitSpend={(spend) => updateSpend.mutate({ id: c.id, spend })}
              />
            ))}
            {summary.campaigns.length === 0 ? (
              <tr>
                <td colSpan={6} className="empty-cell">
                  Brak kanałów. Dodaj pierwszy kanał, aby śledzić wydatki.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
        <div className="cell-sub" style={{ marginTop: 10 }}>
          Koszt reklamy dzieli się równo na leady z danego kanału i wlicza do CAC.
        </div>
      </div>
    </div>
  );
}

/** Number input for a settings value: local draft, committed on blur/Enter, clamped to ≥ 0. */
function RateField({
  label,
  value,
  onCommit,
}: {
  label: string;
  value: number;
  onCommit: (value: number) => void;
}) {
  const [draft, setDraft] = useState<string>(String(value));

  // Re-seed when the server value changes (e.g. after a successful save).
  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = (): void => {
    const next = Math.max(0, Number(draft) || 0);
    if (next !== value) onCommit(next);
    setDraft(String(next));
  };

  const onKeyDown = (ev: KeyboardEvent<HTMLInputElement>): void => {
    if (ev.key === 'Enter') ev.currentTarget.blur();
  };

  return (
    <label className="fld">
      {label}
      <input
        className="input"
        type="number"
        min="0"
        value={draft}
        onChange={(ev) => setDraft(ev.target.value)}
        onBlur={commit}
        onKeyDown={onKeyDown}
      />
    </label>
  );
}

/** Channel row with an editable spend input: local draft, committed on blur, clamped to ≥ 0. */
function CampaignRowView({
  campaign,
  onCommitSpend,
}: {
  campaign: CampaignRow;
  onCommitSpend: (spend: number) => void;
}) {
  const [draft, setDraft] = useState<string>(String(campaign.spend));

  useEffect(() => {
    setDraft(String(campaign.spend));
  }, [campaign.spend]);

  const commit = (): void => {
    const next = Math.max(0, Number(draft) || 0);
    if (next !== campaign.spend) onCommitSpend(next);
    setDraft(String(next));
  };

  const onKeyDown = (ev: KeyboardEvent<HTMLInputElement>): void => {
    if (ev.key === 'Enter') ev.currentTarget.blur();
  };

  return (
    <tr className="no-click">
      <td>
        <div className="cell-main">{campaign.name}</div>
      </td>
      <td className="num">
        <span className="spend-edit">
          <input
            className="input slim mono"
            type="number"
            min="0"
            value={draft}
            onChange={(ev) => setDraft(ev.target.value)}
            onBlur={commit}
            onKeyDown={onKeyDown}
          />{' '}
          zł
        </span>
      </td>
      <td className="num mono">{campaign.leadCount}</td>
      <td className="num mono">{campaign.costPerLead == null ? '—' : fmtZl(campaign.costPerLead)}</td>
      <td className="num mono">{campaign.subCount}</td>
      <td className="num mono">{campaign.costPerSub == null ? '—' : fmtZl(campaign.costPerSub)}</td>
    </tr>
  );
}
